import { Box, CardHeader, createStyles, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import React, { ReactNode } from 'react';
import { ColumnInstance, Filters, IdType, TableInstance, UseGlobalFiltersOptions } from 'react-table';
import DataGridExportMenu from '../DataGridExportMenu';
import DataGridFilterMenu from '../DataGridFilterMenu';
import DataGridSearch from '../DataGridSearch';
import DataGridSettingsMenu from '../DataGridSettingsMenu';
import DataGridTitle from '../DataGridTitle';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardHeader: {
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
    },
    title: {
      '& .MuiCardHeader-title': {
        color: '#263238',
      },
    },
    contextActions: {
      marginLeft: theme.spacing(1),
    },
  }),
);

interface DataGridCardHeaderProps<D extends Record<string, unknown>> {
  overrideTitleColor?: boolean;
  title?: ReactNode;
  handleDebounceSearch: (searchValue: string) => void;
  onExportClick?: (event: React.MouseEvent) => void;
  renderLeftAlignedButtons?: (instance: TableInstance<D>) => ReactNode;
  tableInstance: TableInstance<D>;
  leftAlignedButtons?: ReactNode[];
  rightAlignedButtons?: ReactNode[];
  showExportMenu?: boolean;
  showSettingsMenu?: boolean;
  showFilterMenu?: boolean;
  data?: D[];
  excelExportFileName?: string;
  excelExportSheetName?: string;
  columnInstances: ColumnInstance<D>[];
  filters: Filters<D>;
  setAllFilters: (updater: Filters<D> | ((filters: Filters<D>) => Filters<D>)) => void;
  headers: ColumnInstance<D>[];
  handleChangeColumnVisibility: (hiddenColumn: ColumnInstance<D>) => void;
  handleChangeColumnOrder: (reorderedColumn: IdType<D>[]) => void;
  onReset: () => void;
  tableOptions?: { [key: string]: unknown };
  useGlobalFilterOptions?: UseGlobalFiltersOptions<D>;
  globalFilter: string | undefined;
  searchBarInput?: string;
  toolTipOptions?: { searchToolTipText?: string };
}

export function DataGridCardHeader<T extends Record<string, unknown>>({
  overrideTitleColor = false,
  title,
  handleDebounceSearch,
  onExportClick,
  renderLeftAlignedButtons,
  tableInstance,
  leftAlignedButtons,
  rightAlignedButtons,
  showExportMenu = true,
  showSettingsMenu = true,
  showFilterMenu = true,
  data = [],
  excelExportFileName = 'Download',
  excelExportSheetName = 'Data',
  columnInstances,
  filters,
  setAllFilters,
  headers,
  handleChangeColumnVisibility,
  handleChangeColumnOrder,
  onReset,
  tableOptions,
  useGlobalFilterOptions,
  globalFilter,
  searchBarInput,
  toolTipOptions,
}: DataGridCardHeaderProps<T>) {
  const classes = useStyles();

  return (
    <CardHeader
      className={clsx(classes.cardHeader, overrideTitleColor && classes.title)}
      data-testid="card-header"
      title={
        typeof title === 'string' ? (
          <Box display="flex" alignItems="center" data-testid="box-id">
            <DataGridTitle title={title} />
            <Box display="flex" alignItems="center" className={classes.contextActions}>
              {renderLeftAlignedButtons ? renderLeftAlignedButtons(tableInstance) : leftAlignedButtons}
            </Box>
          </Box>
        ) : (
          <Box display="flex" alignItems="center" data-testid="box-id">
            {title}
            <Box display="flex" alignItems="center" className={classes.contextActions}>
              {renderLeftAlignedButtons ? renderLeftAlignedButtons(tableInstance) : leftAlignedButtons}
            </Box>
          </Box>
        )
      }
      action={
        <Box display="flex" alignItems="center">
          {rightAlignedButtons}
          {showExportMenu && (
            <DataGridExportMenu
              onExportClick={onExportClick}
              fileName={excelExportFileName}
              sheetName={excelExportSheetName}
              table={tableInstance}
            />
          )}

          {showFilterMenu && (
            <DataGridFilterMenu
              data={data}
              columns={(columnInstances as unknown) as ColumnInstance[]}
              filters={filters}
              onSubmit={(filtersArgs) => {
                setAllFilters(filtersArgs as Filters<T>);
              }}
              onReset={() => {
                setAllFilters([]);
              }}
            />
          )}
          {showSettingsMenu ? (
            <DataGridSettingsMenu
              columns={headers}
              columnOrderId={headers.map((c) => c.id)}
              setChangeColumnVisibility={handleChangeColumnVisibility}
              setChangeColumnOrder={handleChangeColumnOrder}
              onReset={onReset}
            />
          ) : (
            ''
          )}
          {(!tableOptions?.disableGlobalFilter || useGlobalFilterOptions?.disableGlobalFilter == false) && (
            <DataGridSearch
              value={globalFilter || searchBarInput}
              onHandleChange={handleDebounceSearch}
              tooltipText={toolTipOptions?.searchToolTipText}
            />
          )}
        </Box>
      }
    />
  );
}
